import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useMatches, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { CollectionContext } from '../contexts/CollectionContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { API_COLLECTIONS_URL, API_ELEMENT_SETS_URL } from '../utils';
import CollectionActions from '../components/collections/CollectionActions';
import ShellHeader from '../components/shell/ShellHeader';
import ShellState, { shellStateOf } from '../components/shell/ShellState';
import { ShellSlotContext } from '../components/shell/ShellSlot';
import { collectionShellConfig } from '../config/collectionShell';
import { leafHandle } from '../config/routeHandles';
import PublishedBy from '../components/publishers/PublishedBy';
import { hierarchyOf } from '../utils/collectionFamily';
import { seedCollectionIdentities } from '../hooks/useCollectionIdentity';
import { isArchiveKind, isGlobal, isPersonal } from '../utils/collectionKinds';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { isGlobalConsumer } from '../utils/permissions';
import { pushRecent } from '../utils/recents';
import { recordView } from '../services/views';
import { toRgba } from '../utils/colors';
import { getSessionKey } from '../utils/session';

export default function CollectionLayout() {
	const { collectionId } = useParams();

	const normalizedCollectionId = typeof collectionId === 'undefined' ? '' : `${collectionId}`;
	const hasResolvedCollectionId = Boolean(normalizedCollectionId)
		&& normalizedCollectionId !== '-1'
		&& normalizedCollectionId !== 'new';
	const resolvedCollectionId = hasResolvedCollectionId ? normalizedCollectionId : null;

	const [collection, setCollection] = useState(null);
	const [collectionLoading, setCollectionLoading] = useState(false);
	const [collectionError, setCollectionError] = useState(null);
	// The session the collection was read under: what it teaches the badge
	// resolver is that account's, never the next one's.
	const fetchedSessionRef = useRef(null);

	const fetchCollection = useCallback(() => {
		if (!resolvedCollectionId) {
			setCollection(null);
			setCollectionError(null);
			setCollectionLoading(false);
			return;
		}

		setCollectionLoading(true);
		setCollectionError(null);

		const session = getSessionKey();
		api.get(`${API_COLLECTIONS_URL}/${resolvedCollectionId}`)
			.then(({ data }) => {
				fetchedSessionRef.current = session;
				setCollection(data);
			})
			.catch(error => {
				setCollectionError(error);
			})
			.finally(() => {
				setCollectionLoading(false);
			});
	}, [resolvedCollectionId]);

	useEffect(() => {
		setCollection(null);
		fetchCollection();
	}, [fetchCollection]);

	// The collection, and the family its document names, are what every
	// qualified badge beneath it may need to say (hooks/useCollectionIdentity).
	// The document is complete; the family summaries are partial, seeded as
	// the server sent them (never through summaryOf's "Untitled collection"
	// stand-in), each with its parent so a same-named sub-collection is told
	// apart.
	useEffect(() => {
		if (!collection) return;
		const session = fetchedSessionRef.current;
		seedCollectionIdentities([collection], { complete: true, session });
		const hierarchy = hierarchyOf(collection);
		if (!hierarchy) return;
		const ancestors = Array.isArray(hierarchy.ancestors) && hierarchy.ancestors.length
			? hierarchy.ancestors
			: (hierarchy.parent ? [hierarchy.parent] : []);
		const family = [
			...ancestors.map((ancestor, index) => (index > 0 ? { ...ancestor, parentName: ancestors[index - 1]?.name } : ancestor)),
			...(Array.isArray(hierarchy.subCollections) ? hierarchy.subCollections : [])
				.map(sub => ({ ...sub, parentName: collection.name })),
		];
		seedCollectionIdentities(family, { session });
	}, [collection]);

	const contextValue = useMemo(() => ({
		collection,
		collectionId,
		resolvedCollectionId,
		collectionLoading,
		collectionError,
		refreshCollection: fetchCollection,
		setCollection,
	}), [collection, collectionId, resolvedCollectionId, collectionLoading, collectionError, fetchCollection]);

	// A GLOBAL collection's cards (and a keepr archive's) are not in the
	// app-wide card list — that list stays free of every published
	// collection's cards — so its own surfaces load them here, once, into the
	// cache: the items page, the home and the lookups all read that cache.
	const cardDefinitions = useOptionalCardDefinitions();
	const addCards = cardDefinitions?.addCards;
	const needsOwnCards = Boolean(collection) && (isGlobal(collection) || isArchiveKind(collection));
	useEffect(() => {
		if (!needsOwnCards || !resolvedCollectionId || typeof addCards !== 'function') return undefined;
		let live = true;
		Promise.all([
			api.get(`${API_COLLECTIONS_URL}/${resolvedCollectionId}/cards`).then(({ data }) => (Array.isArray(data) ? data : [])).catch(() => []),
			api.get(API_ELEMENT_SETS_URL, { params: { collectionId: resolvedCollectionId } }).then(({ data }) => (Array.isArray(data) ? data : [])).catch(() => []),
		]).then(([cards, sets]) => { if (live) addCards(cards, sets); });
		return () => { live = false; };
	}, [needsOwnCards, resolvedCollectionId, addCards]);

	const { isFavoriteCollection, toggleCollectionFavorite } = useFavorites();

	// Until the collection has answered (or failed) its name is unknown: the
	// header draws placeholders for the badge and crumb, not a stand-in word.
	const headerPending = Boolean(resolvedCollectionId) && !collection && !collectionError;
	const headerTitle = collection?.name || 'Collection';

	// The shell's slot: what a page may say to the header (a title, a last
	// crumb; its actions are portaled into `actionsNode`). Pages that have
	// nothing to say never touch it.
	const [slot, setSlot] = useState({ title: null, crumbTail: null });
	const [actionsNode, setActionsNode] = useState(null);
	const [menuActions, setMenuActions] = useState([]);
	const slotValue = useMemo(() => ({ setSlot, actionsNode, menuActions, setMenuActions }), [actionsNode, menuActions]);
	const shellConfig = useMemo(() => collectionShellConfig({
		collection,
		collectionId: resolvedCollectionId,
		title: slot.title || headerTitle,
		crumbTail: slot.crumbTail,
		// A failed load answers who may do what too: nobody, so no Reports or
		// Settings tab for a collection that would not open.
		accessKnown: Boolean(collection) || Boolean(collectionError),
		loading: headerPending,
	}), [collection, collectionError, resolvedCollectionId, slot.title, slot.crumbTail, headerTitle, headerPending]);

	// One header per route. The layout draws the collection's shell header
	// (breadcrumb, badge, tabs, its actions) on the collection's own surfaces —
	// the items list (an item and the add form with it, KPR-638), search,
	// Reports, Settings, Cards and the bare collection route. Each says so on its
	// route (`handle`, config/routeHandles): a route that edits something INSIDE
	// the collection (a multi add page, a chart, a layout) has none and still
	// draws its own bar, with the collection as a breadcrumb segment, until it
	// moves into the shell — so no route ever stacks two headers.
	const routeHandle = leafHandle(useMatches());
	const ownsBar = routeHandle.shell === 'layout';
	const showHeader = Boolean(collectionId) && ownsBar;
	// A collection that would not open (not found, no access, a failed read) is
	// said INSIDE the frame, in the page's place; the header above stays drawn.
	const shellState = !collection ? shellStateOf(collectionError) : 'ready';
	// Settings and Cards hold the collection form (KPR-1281): their primary is Save changes.
	const onSettings = routeHandle.primary === 'save';

	// View history: visiting a collection records it, server-side (the
	// palette and /history read it back) and in the localStorage fallback.
	useEffect(() => {
		if (resolvedCollectionId && collection?.name) {
			recordView('collection', resolvedCollectionId);
			pushRecent({
				type: 'collection',
				id: resolvedCollectionId,
				title: collection.name,
				path: `/collections/${resolvedCollectionId}/items`,
			});
		}
	}, [resolvedCollectionId, collection?.name]);

	// The collection's color reaches the brand chip and the shell header's
	// bottom border (the identity rule, §1; KPR-818); the soft tint still feeds
	// a few hover styles. Only the route's own collection colors it: on a
	// switch the previous one is still held for a render, and its color must
	// not flash on the next collection's header.
	const accentCollection = collection && String(collection._id) === String(resolvedCollectionId) ? collection : null;
	const accentStyle = useMemo(() => {
		const accent = accentCollection?.color;
		if (!accent) return undefined;
		return {
			'--collection-accent': accent,
			'--collection-accent-soft': toRgba(accent, 0.14) || undefined,
		};
	}, [accentCollection?.color]);

	return (
		<CollectionContext.Provider value={contextValue}>
			<ShellSlotContext.Provider value={slotValue}>
				{showHeader ? (
					<ShellHeader
						style={accentStyle}
						config={shellConfig}
						actionsSlotRef={setActionsNode}
						// Who publishes a global collection (KPR-385): the API sends
						// `publisher` only while one stands behind it.
						below={collection?.publisher && isGlobal(collection) ? <PublishedBy publisher={collection.publisher} logo link /> : null}
						state={shellState}
						// The collection's own ⋯ and primary (Add item). The section
						// buttons are the header's tabs now. The personal collection
						// offers no star: it is the rail's first pin already, and
						// cannot be unpinned.
						actions={(
							<CollectionActions
								collection={collection}
								collectionId={resolvedCollectionId}
								isFavorite={isFavoriteCollection(resolvedCollectionId)}
								// Settings and Cards have their own primary (Save changes), and a header
								// carries one filled primary, so Add item steps aside there.
								showPrimary={!onSettings}
								// Nor does a global collection's consumer: favorites are a
								// member's pin, and a reader follows instead.
								onToggleFavorite={isPersonal(collection) || isGlobalConsumer(collection) ? undefined : () => toggleCollectionFavorite(resolvedCollectionId, collection)}
							/>
						)}
					/>
				) : null}

				{showHeader && shellState !== 'ready' ? (
					<ShellState state={shellState} onRetry={fetchCollection} />
				) : <Outlet />}
			</ShellSlotContext.Provider>
		</CollectionContext.Provider>
	);
}
